// Token storage keys
const STORAGE_KEYS = {
	ACCESS_TOKEN: 'drone_access_token',
	REFRESH_TOKEN: 'drone_refresh_token',
	USER_PROFILE: 'drone_user_profile',
	// Add session-related keys that might be used
	SESSION_ID: 'drone_session_id',
	LAST_ACTIVITY: 'drone_last_activity',
} as const;

// Auth token interface (local definition to avoid circular import)
interface AuthTokens {
	accessToken: string;
	refreshToken?: string;
}
class TokenStorage {
	/**
	 * Store auth tokens in localStorage
	 */
	setTokens(tokens: AuthTokens): void {
		if (typeof window !== 'undefined') {
			// Validate tokens before storing but be more lenient
			if (!tokens) {
				console.error('No tokens provided to setTokens');
				return;
			}

			if (!tokens.accessToken) {
				console.error('Access token is required in setTokens:', tokens);
				return;
			}

			try {
				localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
				if (tokens.refreshToken) {
					localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
				}
				console.log('Tokens stored successfully');
			} catch (error) {
				console.error('Failed to store tokens:', error);
				throw error;
			}
		}
	}

	/**
	 * Get access token from localStorage
	 */
	getAccessToken(): string | null {
		if (typeof window !== 'undefined') {
			return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
		}
		return null;
	}

	/**
	 * Get refresh token from localStorage
	 */
	getRefreshToken(): string | null {
		if (typeof window !== 'undefined') {
			return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
		}
		return null;
	}

	/**
	 * Get all tokens from localStorage
	 */
	getTokens(): AuthTokens | null {
		const accessToken = this.getAccessToken();
		const refreshToken = this.getRefreshToken();

		if (accessToken) {
			return {
				accessToken,
				refreshToken: refreshToken || undefined,
			};
		}

		return null;
	}

	/**
	 * Remove all tokens from localStorage
	 */
	removeTokens(): void {
		if (typeof window !== 'undefined') {
			localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
			localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
		}
	}

	/**
	 * Check if user is logged in (has access token)
	 */
	isLoggedIn(): boolean {
		return !!this.getAccessToken();
	}

	/**
	 * Get Authorization header value
	 */
	getAuthorizationHeader(): string | null {
		const token = this.getAccessToken();
		return token ? `Bearer ${token}` : null;
	}

	/**
	 * Store user profile data
	 */
	setUserProfile(profile: any): void {
		if (typeof window !== 'undefined') {
			localStorage.setItem(STORAGE_KEYS.USER_PROFILE, JSON.stringify(profile));
		}
	}

	/**
	 * Get user profile data
	 */
	getUserProfile(): any | null {
		if (typeof window !== 'undefined') {
			const profile = localStorage.getItem(STORAGE_KEYS.USER_PROFILE);
			return profile ? JSON.parse(profile) : null;
		}
		return null;
	}

	/**
	 * Remove user profile data
	 */
	removeUserProfile(): void {
		if (typeof window !== 'undefined') {
			localStorage.removeItem(STORAGE_KEYS.USER_PROFILE);
		}
	}

	/**
	 * Store session ID
	 */
	setSessionId(sessionId: string): void {
		if (typeof window !== 'undefined') {
			localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
		}
	}

	/**
	 * Get session ID
	 */
	getSessionId(): string | null {
		if (typeof window !== 'undefined') {
			return localStorage.getItem(STORAGE_KEYS.SESSION_ID);
		}
		return null;
	}

	/**
	 * Update last activity timestamp
	 */
	updateLastActivity(): void {
		if (typeof window !== 'undefined') {
			localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, new Date().toISOString());
		}
	}

	/**
	 * Clear all auth data (tokens, profile, and session data)
	 */
	clearAuthData(): void {
		if (typeof window !== 'undefined') {
			console.log('tokenStorage.clearAuthData: Starting comprehensive auth data cleanup');

			// Remove all known auth-related localStorage items
			localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
			localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
			localStorage.removeItem(STORAGE_KEYS.USER_PROFILE);
			localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
			localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);

			// Check for last page storage
			localStorage.removeItem('drone_last_page');

			// Clean up any other potential auth-related data
			// Scan for any remaining keys with drone_ prefix and remove them
			const keysToRemove: string[] = [];
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i);
				if (
					key &&
					(key.startsWith('drone_') || key.includes('auth') || key.includes('token'))
				) {
					keysToRemove.push(key);
				}
			}

			// Remove found keys
			keysToRemove.forEach((key) => {
				console.log(`tokenStorage.clearAuthData: Removing additional auth key: ${key}`);
				localStorage.removeItem(key);
			});

			console.log('tokenStorage.clearAuthData: Auth data cleanup completed');
		}
	}
}

// Export singleton instance
export const tokenStorage = new TokenStorage();

// Export convenience functions for direct access
export const getToken = () => tokenStorage.getAccessToken();
export const getAuthHeader = () => tokenStorage.getAuthorizationHeader();
export const isLoggedIn = () => tokenStorage.isLoggedIn();
export const clearAuthData = () => tokenStorage.clearAuthData();
